// src/components/AuthGate.tsx — auth gating + shared loading/error screens.
//
// useCurrentUser() handles the 401 → /login (PIN) / /.auth/login/aad (SSO)
// redirect internally via window.location.replace. RequireAuth additionally
// renders a react-router <Navigate to="/login"> if the hook ever resolves
// with me=null — belt-and-braces in case the hook's hard redirect doesn't
// fire (network error, future refactor). Public routes (/login, /register,
// /forgot-pin) are not wrapped in RequireAuth so useCurrentUser is never
// invoked on them.

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { MaintenanceHoldingPage } from "./MaintenanceHoldingPage";

export function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.paper,
        color: C.ink,
        fontFamily: F.body,
        fontSize: 15,
        fontWeight: 600,
      }}
    >
      Loading…
    </div>
  );
}

export function ErrorScreen({ message }: { message: string }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "32px 20px",
        background: C.paper,
        color: C.ink,
        fontFamily: F.body,
      }}
    >
      <h1 style={{ fontFamily: F.serif, fontSize: 24, fontWeight: 700, margin: "0 0 12px" }}>
        Something went wrong
      </h1>
      <p style={{ color: C.inkSoft, fontWeight: 600, marginTop: 0 }}>{message}</p>
      <p style={{ color: C.inkSoft, fontWeight: 600 }}>
        Try <a href="/.auth/logout">signing out</a> and back in.
      </p>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading, error } = useCurrentUser();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} />;
  if (!me) return <Navigate to="/login" replace />;

  // Phase 9 c14 -- system maintenance. When active, render the holding page
  // for every non-admin. Admins bypass so they can reach User Access and
  // disable the toggle. The check runs before onboarding so a partially-
  // onboarded volunteer doesn't get stuck halfway through the wizard with
  // no way out -- maintenance overrides everything.
  if (me.systemMaintenance?.active && !me.isAdmin) {
    return <MaintenanceHoldingPage state={me.systemMaintenance} />;
  }

  // First-login onboarding redirect. A user whose hub_users.onboarded_at
  // is still null hasn't gone through (or skipped) the wizard yet — push
  // them to /onboarding before they can land anywhere else in the app.
  // The wizard itself is excluded so the redirect doesn't loop.
  if (me.onboarded_at === null && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
